import axios from "axios";
import { Machine, spawn } from "xstate";
import { assign, log, pure, send, sendParent } from "xstate/lib/actions";
import cellsMachine from "./cells-machine";

// An XState state machine for managing one "row window" of report data.
// This is a long-lived machine that loads its row totals and then hangs
// around waiting for requests to load cell data.

const rowWindowMachine = Machine(
    {
        id: "rowWindow",
        initial: "rows",
        states: {
            rows: {
                entry: log(context => context, "rowWindowMachine"),
                invoke: {
                    src: "getRowTotals",
                    onDone: {
                        target: "ready",
                        actions: ["setRowIDs", "sendRowIDs", "sendRowTotals"]
                    },
                    onError: "error"
                }
            },

            error: {},
            ready: {}
        },
        on: {
            COLUMN_WINDOW: {
                actions: ["logColumnWindow", "scheduleCells"]
            },
            CELLS: { actions: "sendCells" }
        }
    },
    {
        actions: {
            scheduleCells: assign({
                cellMachines: (
                    {
                        accountID,
                        podcastID,
                        report,
                        period,
                        feedFilter,
                        cellMachines = {},
                        rowWindow,
                        rowIDs
                    },
                    { columnWindow }
                ) =>
                    cellMachines[columnWindow]
                        ? cellMachines
                        : {
                              ...cellMachines,
                              [columnWindow]: spawn(
                                  cellsMachine.withContext({
                                      accountID,
                                      podcastID,
                                      report,
                                      period,
                                      feedFilter,
                                      rowWindow,
                                      columnWindow,
                                      rowIDs
                                  }),
                                  columnWindow
                              )
                          }
            }),
            logColumnWindow: log(
                ({ rowWindow }, { columnWindow }) => ({ rowWindow, columnWindow }),
                "COLUMN_WINDOW"
            ),
            setRowIDs: assign({
                rowIDs: (context, { data: result }) => result.rowTotals.map(row => row.rowID)
            }),
            sendRowIDs: pure(({ cellMachines = {} }, { data: result }) => {
                const actions = [];
                for (const key in cellMachines) {
                    actions.push(
                        send(
                            { type: "ROW_IDS", rowIDs: result.rowTotals.map(row => row.rowID) },
                            { to: cellMachines[key] }
                        )
                    );
                }
                return actions;
            }),
            sendRowTotals: sendParent((context, { data: result }) => ({
                type: "ROW_TOTALS",
                rowTotals: result.rowTotals.reduce(
                    (acc, row, i) => ({
                        ...acc,
                        [i + result.rowWindow * result.windowRows]: row
                    }),
                    {}
                )
            })),
            sendCells: sendParent(({ rowWindow, windowRows }, { cells, ...event }) => ({
                ...event,
                cells: Object.keys(cells).reduce(
                    (acc, index) => ({
                        ...acc,
                        [parseInt(index) + rowWindow * windowRows]: cells[index]
                    }),
                    {}
                )
            }))
        },
        guards: {
            haveColumnWindow: (context, event) => !!context.columnWindow
        },
        services: {
            getRowTotals: (
                {
                    accountID,
                    podcastID,
                    report,
                    period,
                    reportStart,
                    reportEnd,
                    windowColumns,
                    windowRows,
                    feedFilter,
                    rowWindow
                },
                event
            ) =>
                axios({
                    url: `/api/${accountID}/measurement/reports/${podcastID}/${report}/${period}/row-totals/${reportStart}/${reportEnd}/${rowWindow}`,
                    params: { windowColumns, windowRows, feedFilter }
                }).then(({ data }) => data)
        }
    }
);

export default rowWindowMachine;
